export function createCurrencyFormatter(
  defaultOptions: {
    decimals?: number | null
    minimumFractionDigits?: number
    /** Whether the input value is represented as the dollar i.e. 1.52 vs cents/pence/etc as 152 */
    isFloat?: boolean
    /** Whether to display the currency symbol i.e. $ 1.52 vs 1.52 */
    hideCurrencySymbol?: boolean
    maximumFractionDigits?: number
  } = {},
) {
  const {
    decimals: defaultDecimals = 2,
    minimumFractionDigits: defaultMinimumFractionDigits = 2,
    isFloat: defaultIsFloat = false,
    hideCurrencySymbol: defaultHideCurrencySymbol = false,
    maximumFractionDigits: defaultMaximumFractionDigits,
  } = defaultOptions

  return (
    amount: string | number,
    {
      decimals = defaultDecimals,
      minimumFractionDigits = defaultMinimumFractionDigits,
      maximumFractionDigits = defaultMaximumFractionDigits,
      isFloat = defaultIsFloat,
      hideCurrencySymbol = defaultHideCurrencySymbol,
    }: typeof defaultOptions = {},
  ) => {
    const parsedNumber =
      typeof amount === 'number' ? amount : isFloat ? parseFloat(amount) : parseInt(amount, 10)

    const number = typeof decimals === 'number' ? parsedNumber / 10 ** decimals : parsedNumber

    if (isNaN(number) && typeof amount !== 'number') return amount

    // Ensure minimum and maximum fraction digits are consistent for proper formatting
    const formattedNumber = Math.abs(number).toLocaleString('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
    })

    if (hideCurrencySymbol) {
      return formattedNumber
    }

    return parsedNumber >= 0 ? `$${formattedNumber}` : `-$${formattedNumber}`
  }
}

export const formatCurrency = createCurrencyFormatter()

export const formatCentsAsDollars = createCurrencyFormatter({ decimals: 2 })

/**
 * Formats a number with commas as thousands separators
 * @param value The number to format
 * @returns Formatted number string with commas (e.g., 1,000)
 */
export const formatNumber = (value: number): string =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
